import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import api from '../utils/api';
import { useAuth } from './AuthContext';

// laravel-echo's reverb connector drives pusher-js, and reaches it through the
// global rather than importing it itself.
window.Pusher = Pusher;

const NotificationContext = createContext(null);

const REVERB_KEY = process.env.REACT_APP_REVERB_APP_KEY;
const REVERB_HOST = process.env.REACT_APP_REVERB_HOST;
const REVERB_PORT = Number(process.env.REACT_APP_REVERB_PORT || 443);
const REVERB_SCHEME = process.env.REACT_APP_REVERB_SCHEME || 'https';

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const echoRef = useRef(null);

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    api.get('/notifications/')
      .then(r => {
        setNotifications(r.data);
        setUnreadCount(r.data.filter(n => !n.is_read).length);
      })
      .catch(() => {});

    // Without a configured key there is no socket to open — the feed above
    // still works, it just won't update live.
    if (!REVERB_KEY) return undefined;

    const echo = new Echo({
      broadcaster: 'reverb',
      key: REVERB_KEY,
      wsHost: REVERB_HOST,
      wsPort: REVERB_PORT,
      wssPort: REVERB_PORT,
      forceTLS: REVERB_SCHEME === 'https',
      enabledTransports: ['ws', 'wss'],

      // A custom authorizer rather than static auth headers, so the bearer
      // token is read at call time. Access tokens expire after an hour, and
      // headers captured when Echo was constructed would go stale — a
      // re-subscribe after a reconnect would then 401 and the socket would
      // never recover. Going through `api` also means the existing 401 ->
      // refresh -> retry interceptor covers this request like any other.
      authorizer: (channel) => ({
        authorize: (socketId, callback) => {
          api.post('/broadcasting/auth', {
            socket_id: socketId,
            channel_name: channel.name,
          })
            .then(response => callback(null, response.data))
            .catch(error => callback(error, null));
        },
      }),
    });

    echoRef.current = echo;

    // The leading dot means "this is the literal broadcast name", not a
    // namespaced event class — it matches broadcastAs() on the server.
    echo.private(`notifications.${user.id}`)
      .listen('.notification.created', (payload) => {
        setNotifications(list => {
          // A reconnect can replay an event the feed already has.
          if (list.some(n => n.id === payload.id)) return list;
          return [payload, ...list];
        });
        setUnreadCount(c => c + 1);
      });

    return () => {
      // pusher-js owns reconnection and backoff, so unlike the old raw
      // WebSocket there is no manual retry timer to tear down here.
      echo.leave(`notifications.${user.id}`);
      echo.disconnect();
      echoRef.current = null;
    };
  }, [user]);

  const markRead = async (id) => {
    const target = notifications.find(n => n.id === id);
    if (!target || target.is_read) return;
    setNotifications(list => list.map(n => n.id === id ? { ...n, is_read: true } : n));
    setUnreadCount(c => Math.max(0, c - 1));
    try { await api.post(`/notifications/${id}/read`); } catch { /* optimistic, ignore */ }
  };

  const markAllRead = async () => {
    setNotifications(list => list.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);
    try { await api.post('/notifications/read-all'); } catch { /* optimistic, ignore */ }
  };

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, markRead, markAllRead }}>
      {children}
    </NotificationContext.Provider>
  );
}

export const useNotifications = () => useContext(NotificationContext);
